import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as apiClient from '@/services/apiClient';

import { hasPermissionForUser, hasAnyPermissionForUser } from '@/constants/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (credentials) => {
    const data = await apiClient.login(credentials);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, setUser, isAuthenticated: Boolean(user) }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** True when the user should use the staff admin workspace (not candidate / super-admin). */
export function isStaffUser(user) {
  if (!user) return false;
  if (user.role === 'super_admin') return false;
  return getPortalType(user) === 'staff';
}

/**
 * @deprecated Prefer isStaffUser(user) or getPortalType(user).
 * Kept for call sites that only have a role slug string.
 */
export function isAdminRole(role) {
  return role && role !== 'candidate' && role !== 'super_admin';
}

/**
 * Derive the effective access tier for a user object.
 */
export function getEffectiveRole(user) {
  return user?.portalAccess || user?.role;
}

/** staff → admin layout; candidate → candidate portal */
export function getPortalType(user) {
  if (!user) return 'staff';
  if (user.portalType) return user.portalType;
  return getEffectiveRole(user) === 'candidate' ? 'candidate' : 'staff';
}

/** Permission check with legacy fallback via role presets. */
export function hasPermission(user, permission) {
  return hasPermissionForUser(user, permission);
}

export function hasAnyPermission(user, permissions) {
  return hasAnyPermissionForUser(user, permissions);
}
